import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  id: "exemple_password",
  class: "q-pa-none q-ma-none"
}

import type { PropType } from 'vue';
import { toRefs, ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { QInput, useQuasar } from 'quasar';
import { useFocus } from '@vueuse/core';

// STORES
import { useUiStore } from 'src/stores/ui';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// Personal libs
import { $t } from 'src/i18n/utils';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import {
  IQDialogButton,
  IQDialogPromptOption,
  IQDialogReturn,
} from '@shared/interface/qDialogTemplate.interface';
import {
  requiredRules,
  passwordRules,
  passwordExample,
} from '@shared/lib/form.lib';
import { NotificationTypeEnum } from '@shared/enum/notification.enum';
import { checkPassword } from '@shared/lib/security.lib';
import { IConfigSecurity } from '@shared/interface/config.interface';

/**
 * Properties
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DiagGeneric',
  props: {
  show: { required: false, type: Boolean, default: false },
  manageLoading: { required: false, type: Boolean, default: false },
  title: { required: false, type: String, default: '' },
  message: { required: false, type: String, default: '' },
  persistent: { required: false, type: Boolean, default: false },
  html: { required: false, type: Boolean, default: false },
  passwordDense: { required: false, type: Boolean, default: false },
  dialogSize: {
    required: false,
    type: String as PropType<DialogSizeEnum>,
    default: DialogSizeEnum.SMALL,
  },
  ok: {
    required: false,
    type: Object as PropType<IQDialogButton>,
    default: () => ({ label: '' }),
  },
  cancel: {
    required: false,
    type: Object as PropType<IQDialogButton>,
    default: () => ({}),
  },
  hideCancel: { required: false, type: Boolean, default: false },
  hideClose: { required: false, type: Boolean, default: false },
  inputPrompt: {
    required: false,
    type: Object as PropType<IQDialogPromptOption>,
    default: () => ({}),
  },
  notificationType: {
    required: false,
    type: String as PropType<NotificationTypeEnum>,
  },
},
  emits: ['close', 'cancel', 'valid'],
  setup(__props, { emit: __emit }) {

// Vendor
const props = __props;
const {
  show,
  manageLoading,
  title,
  message,
  persistent,
  html,
  dialogSize,
  ok,
  cancel,
  hideCancel,
  hideClose,
  inputPrompt,
  notificationType,
} = toRefs(props);

/**
 * Stores
 */
const uiStore = useUiStore();

/**
 * Emits
 */
const emits = __emit;

/**
 * Globals
 */
const $q = useQuasar();

/**
 * HTMLElement ref
 */
const qInputTextOrTextarea = ref<QInput>();
const qInputPassword = ref<QInput>();
const { focused: qInputPasswordFocused } = useFocus(qInputPassword);

/**
 * Datas
 */
const password = ref<string | null>(null);
const error = ref<string | null>(null);
const isPwd = ref<boolean | null>(true);
const isSecretCode = ref<boolean | null>(true);
const loading = ref<boolean>(false);
const inputModel = ref<string>('');

/**
 * Computed properties
 */
const securityRules = computed<IConfigSecurity | undefined>(
  () => uiStore.config?.security,
);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

async function onOkClick() {
  if (inputPrompt.value != null) {
    qInputTextOrTextarea.value?.validate();

    if (qInputTextOrTextarea.value?.hasError) {
      return;
    }
  }

  if (manageLoading.value) {
    loading.value = true;
  }

  if (ok.value.handler != null) {
    ok.value.handler(password.value);
  }

  if (ok.value.asyncHandler != null) {
    await ok.value.asyncHandler(password.value);
  }

  emits('valid', {
    inputPassword: password.value,
    inputText: inputModel.value,
  } as IQDialogReturn);
}

function onCancelClick() {
  emits('cancel');
}

function clearPassword() {
  qInputPassword.value?.resetValidation();
  password.value = null;
  qInputPasswordFocused.value = true;
}

/**
 * Watch
 */
watch(
  () => show.value,
  (newVal) => {
    if (newVal === true) {
      loading.value = false;
      inputModel.value = inputPrompt.value.model ?? '';
      password.value = null;
    }
  },
  { immediate: true },
);

/**
 * Hooks
 */
onMounted(() => {
  error.value =
    inputPrompt.value?.attrs && inputPrompt.value?.attrs[0].error
      ? inputPrompt.value.attrs[0].error
      : null;
});

onBeforeUnmount(() => {
  password.value = null;
  error.value = null;
  isPwd.value = null;
  isSecretCode.value = null;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    ref: "qDialogTemplate",
    "show-dialog": _unref(show),
    title: _unref(title),
    maximized: false,
    persistent: _unref(persistent),
    "display-close-button": _unref(hideClose) === false,
    "disable-close-button": loading.value,
    "dialog-size": _unref(dialogSize),
    "is-form": _unref(inputPrompt) != null,
    "notification-type": _unref(notificationType),
    onClose: _cache[7] || (_cache[7] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createCommentVNode(" CONTENT - START "),
      (_ctx.$slots.custom)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _renderSlot(_ctx.$slots, "custom")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (!_unref(html))
              ? (_openBlock(), _createBlock(_component_q_card_section, { key: 0 }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(message)), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : (_openBlock(), _createBlock(_component_q_card_section, { key: 1 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", { innerHTML: _unref(message) }, null, 8 /* PROPS */, _hoisted_3)
                  ]),
                  _: 1 /* STABLE */
                }))
          ])),
      _createCommentVNode(" CONTENT - END "),
      (_unref(inputPrompt) && _unref(inputPrompt).type === 'password')
        ? (_openBlock(), _createBlock(_component_q_card_section, {
            key: 2,
            class: "q-pt-none"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Règles du mot de passe : " + _toDisplayString(_unref($t)('form.password.rules', {
            passwordMin: securityRules.value?.password?.min || 0,
            passwordMax: securityRules.value?.password?.max || 0,
            passwordAuthCar: securityRules.value?.password?.authCar || '*-',
          })), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt) && _unref(inputPrompt).type === 'password')
        ? (_openBlock(), _createBlock(_component_q_card_section, {
            key: 3,
            class: "q-pt-none"
          }, {
            default: _withCtx(() => [
              _createElementVNode("p", _hoisted_4, " Exemple de mot de passe : " + _toDisplayString(_unref(passwordExample)), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt) && _unref(inputPrompt).type === 'password')
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 4 }, {
            default: _withCtx(() => [
              (_unref(inputPrompt) && _unref(inputPrompt).type === 'password')
                ? (_openBlock(), _createBlock(_unref(QInput), {
                    key: 0,
                    ref_key: "qInputPassword",
                    ref: qInputPassword,
                    class: "q-pt-none q-mx-sm",
                    modelValue: password.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                    onKeyup: _withKeys(onOkClick, ["enter"]),
                    "aria-required": "true",
                    "aria-describedby": "exemple_password",
                    "aria-invalid": _unref(checkPassword)(password.value || '') ? 'false' : 'true',
                    autocomplete: "current-password",
                    dense: __props.passwordDense,
                    "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
                    "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                    "input-class": [
            _unref($q).dark.isActive ? 'text-white' : 'text-black',
            'set-outline',
          ],
                    type: isPwd.value ? 'password' : 'text',
                    label: 
            _unref(inputPrompt).label
              ? _unref(inputPrompt).label
              : _unref($t)('generic.input.password.label')
          ,
                    "stack-label": _unref(inputPrompt).stackLabel,
                    "hide-bottom-space": "",
                    "lazy-rules": "",
                    rules: _unref(passwordRules),
                    enterkeyhint: "done"
                  }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_q_icon, { name: "lock" })
                    ]),
                    append: _withCtx(() => [
                      _createVNode(_component_q_btn, {
                        flat: "",
                        round: "",
                        dense: "",
                        title: _unref($t)('form.password.reset'),
                        icon: "cancel",
                        class: "set-outline",
                        onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (clearPassword()), ["stop"]))
                      }, null, 8 /* PROPS */, ["title"]),
                      _createVNode(_component_q_btn, {
                        icon: isPwd.value ? 'visibility_off' : 'visibility',
                        class: "set-outline",
                        flat: "",
                        dense: "",
                        round: "",
                        title: 
                isPwd.value ? _unref($t)('signup.password.show') : _unref($t)('signup.password.hide')
              ,
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (isPwd.value = !isPwd.value))
                      }, null, 8 /* PROPS */, ["icon", "title"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["modelValue", "aria-invalid", "dense", "bg-color", "label-color", "input-class", "type", "label", "stack-label", "rules"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (
          _unref(inputPrompt)?.type != null &&
          ['text', 'textarea'].includes(_unref(inputPrompt).type)
        )
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 5 }, {
            default: _withCtx(() => [
              _createVNode(_unref(QInput), {
                ref_key: "qInputTextOrTextarea",
                ref: qInputTextOrTextarea,
                type: _unref(inputPrompt).type,
                class: "q-pt-none q-mx-sm",
                modelValue: inputModel.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((inputModel).value = $event)),
                onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (_unref(inputPrompt).type === 'text' ? onOkClick() : undefined), ["enter"])),
                autofocus: "",
                autogrow: _unref(inputPrompt).type === 'textarea',
                outlined: _unref(inputPrompt).type === 'textarea',
                counter: 
            _unref(inputPrompt) == null || _unref(inputPrompt)?.maxlength == null
              ? undefined
              : _unref(inputPrompt).maxlength > 0
          ,
                maxlength: 
            _unref(inputPrompt) && _unref(inputPrompt).maxlength
              ? _unref(inputPrompt).maxlength
              : undefined
          ,
                "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
                "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                "input-class": [
            _unref($q).dark.isActive ? 'text-white' : 'text-black',
            'set-outline',
          ],
                label: 
            _unref(inputPrompt).label
              ? `${_unref(inputPrompt).label}${
                  !_unref(inputPrompt).required ? ' (facultatif)' : ''
                }`
              : _unref($t)('generic.input.text.label')
          ,
                "stack-label": _unref(inputPrompt).stackLabel,
                enterkeyhint: "done",
                "hide-bottom-space": "",
                "lazy-rules": "",
                rules: _unref(inputPrompt).required ? _unref(requiredRules) : undefined,
                required: _unref(inputPrompt).required,
                "aria-required": _unref(inputPrompt).required
              }, null, 8 /* PROPS */, ["type", "modelValue", "autogrow", "outlined", "counter", "maxlength", "bg-color", "label-color", "input-class", "label", "stack-label", "rules", "required", "aria-required"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt) && _unref(inputPrompt).type === 'number')
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 6 }, {
            default: _withCtx(() => [
              _createVNode(_unref(QInput), {
                modelValue: password.value,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((password).value = $event)),
                onKeyup: _withKeys(onOkClick, ["enter"]),
                dense: "",
                autofocus: "",
                "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
                "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                type: isSecretCode.value ? 'number' : 'text',
                label: 
            _unref(inputPrompt).label
              ? _unref(inputPrompt).label
              : _unref($t)('generic.input.secretCode.label')
          ,
                "input-class": [
            {
              'text-white': _unref($q).dark.isActive,
              'text-black': !_unref($q).dark.isActive,
              'input-type-number-disc': isSecretCode.value,
            },
            'set-outline',
          ],
                "stack-label": _unref(inputPrompt).stackLabel,
                pattern: "[0-9]*",
                hint: "",
                "lazy-rules": "",
                rules: [
            (val) =>
              (val && val.length > 0) ||
              (error.value ? error.value : _unref($t)('generic.input.secretCode.error')),
          ],
                enterkeyhint: "done"
              }, {
                prepend: _withCtx(() => [
                  _createVNode(_component_q_icon, { name: "lock" })
                ]),
                append: _withCtx(() => [
                  _createVNode(_component_q_btn, {
                    icon: isSecretCode.value ? 'visibility_off' : 'visibility',
                    class: "set-outline",
                    flat: "",
                    dense: "",
                    round: "",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (isSecretCode.value = !isSecretCode.value))
                  }, null, 8 /* PROPS */, ["icon"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["modelValue", "bg-color", "label-color", "type", "label", "input-class", "stack-label", "rules"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_separator),
      _createVNode(_component_q_card_actions, { align: "right" }, {
        default: _withCtx(() => [
          (!_unref(hideCancel))
            ? (_openBlock(), _createBlock(QBtnCancel, {
                key: 0,
                label: _unref(cancel).label ? _unref(cancel).label : _unref($t)('generic.btn.cancel.label'),
                disable: loading.value,
                loading: loading.value,
                onClick: onCancelClick
              }, null, 8 /* PROPS */, ["label", "disable", "loading"]))
            : _createCommentVNode("v-if", true),
          _createVNode(QBtnValid, {
            label: _unref(ok).label ? _unref(ok).label : _unref($t)('generic.btn.validate.label'),
            disable: loading.value,
            loading: loading.value,
            "loading-label": '',
            "notification-type": _unref(notificationType),
            onClick: onOkClick
          }, null, 8 /* PROPS */, ["label", "disable", "loading", "notification-type"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["show-dialog", "title", "persistent", "display-close-button", "disable-close-button", "dialog-size", "is-form", "notification-type"]))
}
}

})